import React, { useState, useEffect } from 'react';
import { X, Save, MapPin, Euro, Star } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { featuredPropertiesService } from '../../../services/featuredPropertiesService';

const PropertyFormModal = ({ property, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    price: '',
    market_type: 'sale', // ✅ NEW: Default to sale
    rental_period: '', // ✅ NEW: Rental period for rental properties
    property_type: 'apartment',
    status: 'available',
    bedrooms: '',
    bathrooms: '',
    surface_area: '',
    description: '',
    commune_id: '',
    agent_id: '',
    owner_id: '',
    features: {},
    images: [],
    videos: []
  });
  
  const [communes, setCommunes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [makeItFeatured, setMakeItFeatured] = useState(false);
  const [featuredData, setFeaturedData] = useState({
    custom_headline: '',
    promotional_text: '',
    promotional_price: '',
    hero_image_url: '',
    display_order: 1
  });

  // ✅ Add helper function to determine if property type is land/terrain
  const isLandProperty = () => {
    return formData?.property_type === 'land';
  };

  // ✅ NEW: Helper function to determine if property is for rental
  const isRentalProperty = () => {
    return formData?.market_type === 'rental';
  };

  // ✅ NEW: Helper function to check if property is furnished
  const isFurnishedProperty = () => {
    return formData?.features?.furnished === true;
  };

  // ✅ NEW: Helper function to automatically set daily for furnished rentals
  const handleFurnishedChange = (value) => {
    const newFeatures = {
      ...formData?.features,
      furnished: value
    };
    
    const newFormData = {
      ...formData,
      features: newFeatures
    };
    
    // If property is rental and being marked as furnished, automatically set to daily
    if (formData?.market_type === 'rental' && value === true) {
      newFormData.rental_period = 'daily';
    }
    
    setFormData(newFormData);
  };

  // ✅ UPDATED: Enhanced price label to include daily pricing
  const getPriceLabel = () => {
    if (formData?.market_type === 'sale') {
      return 'Prix de vente (FCFA) *';
    }
    if (formData?.market_type === 'rental') {
      if (formData?.rental_period === 'daily') {
        return 'Prix par jour (FCFA) *';
      }
      if (formData?.rental_period === 'monthly') {
        return 'Loyer mensuel (FCFA) *';
      }
      if (formData?.rental_period === 'yearly') {
        return 'Loyer annuel (FCFA) *';
      }
      return 'Montant du loyer (FCFA) *';
    }
    return 'Prix (FCFA) *';
  };

  // ✅ UPDATED: Enhanced price placeholder to include daily pricing
  const getPricePlaceholder = () => {
    if (formData?.market_type === 'sale') {
      return 'Ex: 25000000 (pour 25 millions FCFA)';
    }
    if (formData?.market_type === 'rental') {
      if (formData?.rental_period === 'daily') {
        return 'Ex: 25000 (pour 25,000 FCFA/jour)';
      }
      if (formData?.rental_period === 'monthly') {
        return 'Ex: 350000 (pour 350,000 FCFA/mois)';
      }
      if (formData?.rental_period === 'yearly') {
        return 'Ex: 4200000 (pour 4,200,000 FCFA/an)';
      }
      return 'Montant du loyer';
    }
    return 'Prix en FCFA';
  };

  useEffect(() => {
    if (isOpen) {
      fetchOptions();
      if (property) {
        setFormData({
          title: property?.title || '',
          address: property?.address || '',
          price: property?.price || '',
          market_type: property?.market_type || 'sale', // ✅ NEW: Include market_type
          rental_period: property?.rental_period || '', // ✅ NEW: Include rental_period
          property_type: property?.property_type || 'apartment',
          status: property?.status || 'available',
          bedrooms: property?.bedrooms || '',
          bathrooms: property?.bathrooms || '',
          surface_area: property?.surface_area || '',
          description: property?.description || '',
          commune_id: property?.commune_id || '',
          agent_id: property?.agent_id || '',
          owner_id: property?.owner_id || '',
          features: property?.features || {},
          images: property?.images || [],
          videos: property?.videos || []
        });
        setMakeItFeatured(false);
      } else {
        setFormData({
          title: '',
          address: '',
          price: '',
          market_type: 'sale', // ✅ NEW: Default to sale for new properties
          rental_period: '', // ✅ NEW: Empty for new properties
          property_type: 'apartment',
          status: 'available',
          bedrooms: '',
          bathrooms: '',
          surface_area: '',
          description: '',
          commune_id: '',
          agent_id: '',
          owner_id: '',
          features: {},
          images: [],
          videos: []
        });
        setMakeItFeatured(false);
        setFeaturedData({
          custom_headline: '',
          promotional_text: '',
          promotional_price: '',
          hero_image_url: '',
          display_order: 1
        });
      }
    }
  }, [isOpen, property]);

  const fetchOptions = async () => {
    try {
      const [communesRes, agentsRes, ownersRes] = await Promise.all([
        supabase?.from('communes')?.select('id, name, district')?.order('name'),
        supabase?.from('user_profiles')?.select('id, full_name, email')?.eq('role', 'agent')?.order('full_name'),
        supabase?.from('user_profiles')?.select('id, full_name, email')?.eq('role', 'client')?.order('full_name')
      ]);

      // Format data for enhanced Select component - same pattern as property creation dashboard
      const formattedCommunes = (communesRes?.data || [])?.map(commune => ({
        value: commune?.id,
        label: `${commune?.name} (${commune?.district})`
      }));

      const formattedAgents = (agentsRes?.data || [])?.map(agent => ({
        value: agent?.id,
        label: `${agent?.full_name} (${agent?.email})`
      }));

      const formattedOwners = (ownersRes?.data || [])?.map(owner => ({
        value: owner?.id,
        label: `${owner?.full_name} (${owner?.email})`
      }));

      setCommunes(formattedCommunes);
      setAgents(formattedAgents);
      setOwners(formattedOwners);

      console.log('✅ PropertyFormModal options loaded:', { 
        communes: formattedCommunes?.length, 
        agents: formattedAgents?.length, 
        owners: formattedOwners?.length 
      });
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const propertyData = {
        ...formData,
        price: parseFloat(formData?.price) || 0,
        market_type: formData?.market_type, // ✅ NEW: Include market_type
        rental_period: formData?.market_type === 'rental' ? formData?.rental_period : null, // ✅ NEW: Only set rental_period for rentals
        bedrooms: parseInt(formData?.bedrooms) || null,
        bathrooms: parseInt(formData?.bathrooms) || null,
        surface_area: parseFloat(formData?.surface_area) || null,
        commune_id: formData?.commune_id || null,
        agent_id: formData?.agent_id || null,
        owner_id: formData?.owner_id || null,
        features: formData?.features || {},
        images: formData?.images || [],
        videos: formData?.videos || []
      };

      let savedProperty;
      
      if (property) {
        // Update existing property
        const { error } = await supabase?.from('properties')
          ?.update(propertyData)
          ?.eq('id', property?.id);

        if (error) throw error;
        savedProperty = { id: property?.id, ...propertyData };
      } else {
        // Create new property
        const { data: newProperty, error } = await supabase?.from('properties')
          ?.insert(propertyData)
          ?.select('*')
          ?.single();

        if (error) throw error;
        savedProperty = newProperty;

        // If user selected "Make Featured", create featured property entry
        if (makeItFeatured && savedProperty) {
          const featuredPropertyData = {
            property_id: savedProperty?.id,
            custom_headline: featuredData?.custom_headline || savedProperty?.title,
            promotional_text: featuredData?.promotional_text || '',
            promotional_price: parseFloat(featuredData?.promotional_price) || null,
            hero_image_url: featuredData?.hero_image_url || savedProperty?.images?.[0] || null,
            display_order: parseInt(featuredData?.display_order) || 1,
            is_active: true
          };

          const { error: featuredError } = await featuredPropertiesService?.createFeaturedProperty(featuredPropertyData);
          
          if (featuredError) {
            // Log the error but don't fail the entire operation 
            console.warn('Failed to feature property:', featuredError);
          }
        }
      }

      onSave?.();
      onClose?.();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (feature, value) => {
    setFormData({
      ...formData,
      features: {
        ...formData?.features,
        [feature]: value
      }
    });
  };

  // ✅ NEW: Handler for market type change - reset rental period when switching to sale
  const handleMarketTypeChange = (value) => {
    setFormData({
      ...formData,
      market_type: value,
      rental_period: value === 'sale' ? '' : formData?.rental_period
    });
  };

  // ✅ Add market type options
  const marketTypeOptions = [
    { value: 'sale', label: 'Vente' },
    { value: 'rental', label: 'Location' }
  ];

  // ✅ Add rental period options - UPDATED to include daily
  const rentalPeriodOptions = [
    { value: 'daily', label: 'Journalier' },
    { value: 'monthly', label: 'Mensuel' },
    { value: 'yearly', label: 'Annuel' }
  ];

  // ✅ UPDATED: Enhanced price conversion helper for all periods
  const priceConversionHelper = () => {
    if (isRentalProperty() && formData?.price && formData?.rental_period) {
      if (formData?.rental_period === 'daily') {
        return (
          <>
            {(parseFloat(formData?.price || 0) * 30)?.toLocaleString()} FCFA/mois • {' '}
            {(parseFloat(formData?.price || 0) * 365)?.toLocaleString()} FCFA/an
          </>
        );
      }
      if (formData?.rental_period === 'monthly') {
        return (
          <>
            {(parseFloat(formData?.price || 0) / 30)?.toLocaleString()} FCFA/jour • {' '}
            {(parseFloat(formData?.price || 0) * 12)?.toLocaleString()} FCFA/an
          </>
        );
      }
      return (
        <>
          {(parseFloat(formData?.price || 0) / 365)?.toLocaleString()} FCFA/jour • {' '}
          {(parseFloat(formData?.price || 0) / 12)?.toLocaleString()} FCFA/mois
        </>
      );
    }
    return null;
  };

  // ✅ NEW: Furnished Property Notification
  const furnishedPropertyNotification = () => {
    if (isRentalProperty() && isFurnishedProperty() && formData?.rental_period !== 'daily') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
          <div className="flex items-center gap-2">
            <span className="text-yellow-600">⚠️</span>
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Propriété meublée détectée
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Les résidences meublées sont généralement louées à la journée. 
                <button
                  type="button"
                  onClick={() => setFormData({...formData, rental_period: 'daily'})}
                  className="ml-1 underline hover:no-underline font-medium"
                >
                  Passer en location journalière
                </button>
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // ✅ UPDATED: Market Type Summary Card with daily support
  const marketTypeSummaryCard = () => {
    if (formData?.price && formData?.rental_period) {
      return (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">
                Résumé de la transaction
              </h4>
              <p className="text-sm text-gray-600">
                {formData?.market_type === 'sale' ? 
                  `🏠 Propriété à vendre` : 
                  `🏠 Propriété en location ${formData?.rental_period ? 
                    `(${formData?.rental_period === 'daily' ? 'journalière' : 
                        formData?.rental_period === 'monthly' ? 'mensuelle' : 'annuelle'})` : ''}`
                }
                {isFurnishedProperty() && ' • Meublée'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-green-600">
                {parseFloat(formData?.price)?.toLocaleString()} FCFA
                {isRentalProperty() && formData?.rental_period && 
                  `/${formData?.rental_period === 'daily' ? 'jour' : 
                      formData?.rental_period === 'monthly' ? 'mois' : 'an'}`
                }
              </p>
              {isFurnishedProperty() && (
                <p className="text-xs text-blue-600">
                  🛏️ Résidence meublée
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // ✅ Property Details - Hide bedrooms and bathrooms for land properties
  const propertyDetails = () => {
    if (isLandProperty()) {
      return (
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Surface (m²) *
            </label>
            <Input
              type="number"
              value={formData?.surface_area}
              onChange={(e) => setFormData({...formData, surface_area: e?.target?.value})}
              min="0"
              step="0.1"
              placeholder="Surface du terrain en m²"
              required
            />
          </div>
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>🏞️ Terrain:</strong> Pour les terrains, seules la surface et les caractéristiques générales s'appliquent. 
              Les informations sur les chambres et salles de bain ne sont pas pertinentes.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Chambres
          </label>
          <Input
            type="number"
            value={formData?.bedrooms}
            onChange={(e) => setFormData({...formData, bedrooms: e?.target?.value})}
            min="0"
            placeholder="Nombre de chambres"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Salles de bain
          </label>
          <Input
            type="number"
            value={formData?.bathrooms}
            onChange={(e) => setFormData({...formData, bathrooms: e?.target?.value})}
            min="0"
            placeholder="Nombre de salles de bain"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Surface (m²)
          </label>
          <Input
            type="number"
            value={formData?.surface_area}
            onChange={(e) => setFormData({...formData, surface_area: e?.target?.value})}
            min="0"
            step="0.1"
            placeholder="Surface habitable"
          />
        </div>
      </div>
    );
  };

  // ✅ Features - Show all features but with better organization for land
  const features = () => {
    if (isLandProperty()) {
      return (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Sélectionnez les caractéristiques applicables à ce terrain:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { key: 'parking', label: 'Stationnement disponible' },
              { key: 'security', label: 'Sécurité' },
              { key: 'utilities', label: 'Raccordements (eau, électricité)' },
              { key: 'garden', label: 'Espace vert aménageable' },
              { key: 'fenced', label: 'Terrain clôturé' },
              { key: 'buildable', label: 'Constructible' }
            ]?.map((feature) => (
              <label key={feature?.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData?.features?.[feature?.key] || false}
                  onChange={(e) => handleFeatureChange(feature?.key, e?.target?.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{feature?.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { key: 'pool', label: 'Piscine' },
          { key: 'garden', label: 'Jardin' },
          { key: 'parking', label: 'Parking' },
          { key: 'garage', label: 'Garage' },
          { key: 'balcony', label: 'Balcon' },
          { key: 'elevator', label: 'Ascenseur' },
          { key: 'furnished', label: 'Meublé', special: true },
          { key: 'security', label: 'Sécurité' }
        ]?.map((feature) => (
          <label key={feature?.key} className="flex items-center">
            <input
              type="checkbox"
              checked={formData?.features?.[feature?.key] || false}
              onChange={(e) => {
                if (feature?.special && feature?.key === 'furnished') {
                  handleFurnishedChange(e?.target?.checked);
                } else {
                  handleFeatureChange(feature?.key, e?.target?.checked);
                }
              }}
              className={`rounded border-gray-300 focus:ring-blue-500 ${
                feature?.special ? 'text-orange-600' : 'text-blue-600'
              }`}
            />
            <span className={`ml-2 text-sm ${
              feature?.special && formData?.features?.[feature?.key] ? 
              'text-orange-700 font-medium' : 'text-gray-700'
            }`}>
              {feature?.label}
              {feature?.special && formData?.features?.[feature?.key] && ' 🛏️'}
            </span>
          </label>
        ))}
      </div>
    );
  };

  // ✅ NEW: Furnished Property Information Panel
  const furnishedPropertyInfoPanel = () => {
    if (isFurnishedProperty()) {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-orange-600 text-lg">🛏️</span>
            <div>
              <h4 className="font-medium text-orange-900 mb-2">
                Résidence meublée
              </h4>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• Idéale pour la location journalière</li>
                <li>• Équipements et mobilier inclus</li>
                <li>• Tarification généralement plus élevée</li>
                <li>• Clientèle: voyageurs d'affaires, touristes, séjours temporaires</li>
              </ul>
              {formData?.market_type === 'rental' && formData?.rental_period === 'daily' && (
                <div className="mt-3 p-2 bg-green-100 rounded border border-green-300">
                  <p className="text-xs text-green-700 font-medium">
                    ✅ Configuration optimale: Location journalière pour résidence meublée
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose}></div>
        
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-screen overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {property ? 'Modifier la Propriété' : 'Nouvelle Propriété'}
              </h2>
              <p className="text-sm text-gray-500">
                {property ? 'Modifiez les informations de la propriété' : 'Ajoutez une nouvelle propriété au système'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* ✅ Featured Property Option - Only show for new properties */}
            {!property && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Star className="h-5 w-5 text-yellow-600" />
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={makeItFeatured}
                      onChange={(e) => setMakeItFeatured(e?.target?.checked)}
                      className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      Mettre en vedette sur la page d'accueil
                    </span>
                  </label>
                </div>
                
                {makeItFeatured && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Titre personnalisé (optionnel)
                      </label>
                      <Input
                        value={featuredData?.custom_headline}
                        onChange={(e) => setFeaturedData({...featuredData, custom_headline: e?.target?.value})}
                        placeholder="Laissez vide pour utiliser le titre de la propriété"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prix promotionnel (optionnel)
                      </label>
                      <Input
                        type="number"
                        value={featuredData?.promotional_price}
                        onChange={(e) => setFeaturedData({...featuredData, promotional_price: e?.target?.value})}
                        placeholder="Prix spécial pour la promotion"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Texte promotionnel (optionnel)
                      </label>
                      <Input
                        value={featuredData?.promotional_text}
                        onChange={(e) => setFeaturedData({...featuredData, promotional_text: e?.target?.value})}
                        placeholder="Ex: Offre limitée, Prix réduit, etc."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Image de couverture (URL)
                      </label>
                      <Input
                        value={featuredData?.hero_image_url}
                        onChange={(e) => setFeaturedData({...featuredData, hero_image_url: e?.target?.value})}
                        placeholder="URL de l'image principale"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ordre d'affichage
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={featuredData?.display_order}
                        onChange={(e) => setFeaturedData({...featuredData, display_order: e?.target?.value})}
                        placeholder="1"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Informations de base
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre de la propriété *
                  </label>
                  <Input
                    value={formData?.title}
                    onChange={(e) => setFormData({...formData, title: e?.target?.value})}
                    placeholder="Ex: Villa moderne avec piscine"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={formData?.address}
                      onChange={(e) => setFormData({...formData, address: e?.target?.value})}
                      placeholder="Adresse complète"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* ✅ NEW: Market Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de transaction *
                  </label>
                  <Select
                    value={formData?.market_type}
                    onChange={handleMarketTypeChange}
                    options={marketTypeOptions}
                    placeholder="Vente ou Location"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Choisissez si la propriété est à vendre ou à louer
                  </p>
                </div>

                {/* ✅ NEW: Rental Period Selection - Only show for rental properties */}
                {isRentalProperty() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Période de location *
                    </label>
                    <Select
                      value={formData?.rental_period}
                      onChange={(value) => setFormData({...formData, rental_period: value})}
                      options={rentalPeriodOptions}
                      placeholder="Mensuel ou Annuel"
                      required
                    />
                    <p className="mt-1 text-xs text-blue-600">
                      Le prix sera affiché par {
                        formData?.rental_period === 'daily' ? 'jour' : 
                        formData?.rental_period === 'monthly' ? 'mois' : 'année'
                      }
                    </p>
                  </div>
                )}

                {/* ✅ UPDATED: Dynamic Price Field with Smart Labeling */}
                <div className={isRentalProperty() && !formData?.rental_period ? 'opacity-50' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getPriceLabel()}
                  </label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={formData?.price}
                      onChange={(e) => setFormData({...formData, price: e?.target?.value})}
                      placeholder={getPricePlaceholder()}
                      className="pl-10"
                      required
                      disabled={isRentalProperty() && !formData?.rental_period}
                    />
                  </div>
                  {isRentalProperty() && !formData?.rental_period && (
                    <p className="mt-1 text-xs text-amber-600">
                      Sélectionnez d'abord la période de location
                    </p>
                  )}
                  {/* ✅ UPDATED: Enhanced price conversion helper for all periods */}
                  {isRentalProperty() && formData?.price && formData?.rental_period && (
                    <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                      <p className="text-xs text-blue-700">
                        💡 <strong>Équivalences:</strong> {' '}
                        {formData?.rental_period === 'daily' ? (
                          <>
                            {(parseFloat(formData?.price || 0) * 30)?.toLocaleString()} FCFA/mois • {' '}
                            {(parseFloat(formData?.price || 0) * 365)?.toLocaleString()} FCFA/an
                          </>
                        ) : formData?.rental_period === 'monthly' ? (
                          <>
                            {(parseFloat(formData?.price || 0) / 30)?.toLocaleString()} FCFA/jour • {' '}
                            {(parseFloat(formData?.price || 0) * 12)?.toLocaleString()} FCFA/an
                          </>
                        ) : (
                          <>
                            {(parseFloat(formData?.price || 0) / 365)?.toLocaleString()} FCFA/jour • {' '}
                            {(parseFloat(formData?.price || 0) / 12)?.toLocaleString()} FCFA/mois
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* ✅ NEW: Furnished Property Notification */}
                {furnishedPropertyNotification()}

                {/* ✅ UPDATED: Market Type Summary Card with daily support */}
                {marketTypeSummaryCard()}

                {/* ✅ Property Details - Hide bedrooms and bathrooms for land properties */}
                {propertyDetails()}

                {/* ✅ Features - Show all features but with better organization for land */}
                {features()}

                {/* ✅ NEW: Furnished Property Information Panel */}
                {furnishedPropertyInfoPanel()}

                {/* ✅ Attribution - Enhanced functional dropdowns */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Attribution
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agent assigné
                      </label>
                      <Select
                        value={formData?.agent_id}
                        onChange={(value) => setFormData({...formData, agent_id: value})}
                        options={agents}
                        placeholder="Sélectionner un agent"
                        searchable
                        clearable
                        loading={agents?.length === 0}
                      />
                      {agents?.length === 0 && (
                        <p className="mt-1 text-xs text-gray-500">
                          Chargement des agents disponibles...
                        </p>
                      )}
                      {agents?.length > 0 && !formData?.agent_id && (
                        <p className="mt-1 text-xs text-blue-600">
                          {agents?.length} agent(s) disponible(s) dans le système
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Propriétaire
                      </label>
                      <Select
                        value={formData?.owner_id}
                        onChange={(value) => setFormData({...formData, owner_id: value})}
                        options={owners}
                        placeholder="Sélectionner un propriétaire"
                        searchable
                        clearable
                        loading={owners?.length === 0}
                      />
                      {owners?.length === 0 && (
                        <p className="mt-1 text-xs text-gray-500">
                          Chargement des propriétaires disponibles...
                        </p>
                      )}
                      {owners?.length > 0 && !formData?.owner_id && (
                        <p className="mt-1 text-xs text-blue-600">
                          {owners?.length} propriétaire(s) disponible(s) dans le système
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">
                      ✅ <strong>Attribution fonctionnelle:</strong> Les listes déroulantes sont maintenant alimentées en temps réel depuis la base de données avec recherche et sélection complètes.
                    </p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData?.description}
                    onChange={(e) => setFormData({...formData, description: e?.target?.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={isLandProperty() ? 
                      "Description détaillée du terrain: localisation, potentiel de construction, accès, etc..." : 
                      "Description détaillée de la propriété..."
                    }
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || (isRentalProperty() && !formData?.rental_period)}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {loading ? 'Sauvegarde...' : (property ? 'Mettre à jour' : 'Créer')}
                    {!property && makeItFeatured && (
                      <Star className="h-4 w-4 text-yellow-400" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PropertyFormModal;